'use client';

import { Button } from '@/components/Buttons/Button';
import { Spinner } from '@/components/Loaders/Spinner';
import { isFeatureEnabled, type FeatureFlagKey } from '@/constants';
import { TUserRole } from '@/types';
import { useAuth } from '@/components/Auth';
import { useSimulationRouting } from '@/hooks/useSimulationRouting';
import React from 'react';

interface ProtectedRouteProps {
  /** The content to render if access is granted */
  children: React.ReactNode;
  /** Allowed user roles to access this route */
  allowedRoles?: Array<TUserRole>;
  /** Fallback UI to display while loading or when access is denied */
  fallback?: React.ReactNode;
  /** Feature flags required to access this route */
  featureFlags?: FeatureFlagKey[];
}

/**
 * ProtectedRoute component to guard routes based on user authentication and roles.
 * Allows access only to authenticated users with specified roles and feature flags.
 */
export function ProtectedRoute({
  children,
  allowedRoles,
  fallback,
  featureFlags,
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { navigateToDashboard } = useSimulationRouting();
  const handleGoBackClick = () => navigateToDashboard();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      fallback || (
        <div className="flex h-screen max-h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <Spinner size={48} />
            <p className="typography-body text-semantic-text-secondary">
              {!isAuthenticated ? 'Checking authentication...' : 'Loading ...'}
            </p>
          </div>
        </div>
      )
    );
  }

  // If not authenticated, the AuthProvider will handle redirection
  if (!isAuthenticated || !user) {
    return null;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="mt-12 flex h-full items-center justify-center">
        <div className="max-w-md text-center">
          <h1 className="typography-section-title mb-4 text-semantic-text-primary">
            Access Denied
          </h1>
          <p className="typography-body mb-6 text-semantic-text-secondary">
            You don&apos;t have permission to access this page.
          </p>
          <Button label="Go back" variant="link" onClick={handleGoBackClick} />
        </div>
      </div>
    );
  }

  // Check feature flag-based access if featureFlags is specified
  if (featureFlags && !isFeatureEnabled(featureFlags)) {
    return (
      <div className="mt-12 flex h-full items-center justify-center">
        <div className="max-w-md text-center">
          <h1 className="typography-section-title mb-4 text-semantic-text-primary">Coming Soon</h1>
          <p className="typography-body mb-6 text-semantic-text-secondary">
            This feature is not yet available. Please try again later.
          </p>
          <Button label="Go back" variant="link" onClick={handleGoBackClick} />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
